import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  forwardRef,
  input,
  signal,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  getCustomOptionText,
  isCustomOptionValue,
  toCustomOptionValue
} from '@shared/helpers/custom-option-value.helper';

export interface IChipSelectOption {
  value: string;
  /** Text or translation key. */
  label: string;
  /** Rendered in the softer "unknown / none" style. */
  muted?: boolean;
}

export type ChipSelectValue = string | string[] | null;

/**
 * Chips for single or multiple choice (ControlValueAccessor).
 * - single: string | null, clicking the selected chip clears it;
 * - multiple: string[] | null, `exclusiveValues` (e.g. "unknown", "none") replace every other choice;
 * - `allowCustom` adds a "+ own value" chip storing the text as `custom:<text>`. Several own values can be added.
 *   In single mode they replace the chosen option (and an option replaces them), so the value is one option,
 *   one own value, or a string[] of own values. `maxCustom` limits their number, `customWide` gives the input
 *   the whole row.
 */
@Component({
  selector: 'chip-select',
  templateUrl: './chip-select.component.html',
  styleUrls: ['./chip-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => ChipSelectComponent), multi: true }]
})
export class ChipSelectComponent implements ControlValueAccessor {
  options = input.required<IChipSelectOption[]>();
  multiple = input<boolean>(false);
  exclusiveValues = input<string[]>([]);
  allowCustom = input<boolean>(false);
  customLabel = input<string>('ChipSelect.addCustom');
  customPlaceholder = input<string>('ChipSelect.customPlaceholder');
  customMaxLength = input<number>(60);
  customInputMode = input<string | null>(null);
  customWide = input<boolean>(false);
  maxCustom = input<number | null>(null);

  @ViewChild('customInput') customInput: ElementRef<HTMLInputElement>;

  protected readonly value = signal<ChipSelectValue>(null);
  protected readonly disabled = signal(false);
  protected readonly editingCustom = signal(false);

  protected readonly selected = computed(() => {
    const value = this.value();
    return Array.isArray(value) ? value : value ? [value] : [];
  });
  protected readonly customValues = computed(() =>
    this.selected().filter((value) => isCustomOptionValue(value))
  );
  protected readonly canAddCustom = computed(() => {
    const max = this.maxCustom();
    return this.allowCustom() && (max === null || this.customValues().length < max);
  });

  private onChange: (value: ChipSelectValue) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: ChipSelectValue): void {
    this.value.set(value ?? null);
  }

  registerOnChange(fn: (value: ChipSelectValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  protected isSelected(option: IChipSelectOption): boolean {
    return this.selected().includes(option.value);
  }

  protected toggle(option: IChipSelectOption): void {
    if (this.disabled()) return;

    if (!this.multiple()) {
      this.update(this.isSelected(option) ? null : option.value);
      return;
    }

    if (this.isSelected(option)) {
      this.update(this.selected().filter((value) => value !== option.value));
      return;
    }

    const exclusive = this.exclusiveValues();
    this.update(
      exclusive.includes(option.value)
        ? [option.value]
        : [...this.selected().filter((value) => !exclusive.includes(value)), option.value]
    );
  }

  protected getCustomText(value: string): string {
    return getCustomOptionText(value);
  }

  protected removeCustom(value: string): void {
    if (this.disabled()) return;
    const remaining = this.selected().filter((selected) => selected !== value);
    this.update(this.multiple() ? remaining : this.toSingleValue(remaining));
  }

  protected startCustom(): void {
    if (this.disabled()) return;
    this.editingCustom.set(true);
    setTimeout(() => this.customInput?.nativeElement.focus());
  }

  protected confirmCustom(): void {
    const text = this.customInput?.nativeElement.value.trim().slice(0, this.customMaxLength());
    this.editingCustom.set(false);
    if (!text) return;

    const customValue = toCustomOptionValue(text);
    if (this.selected().includes(customValue)) return;
    if (!this.multiple()) {
      this.update(this.toSingleValue([...this.customValues(), customValue]));
      return;
    }

    const exclusive = this.exclusiveValues();
    this.update([...this.selected().filter((value) => !exclusive.includes(value)), customValue]);
  }

  protected cancelCustom(): void {
    this.editingCustom.set(false);
  }

  /** Single mode: one value as a string, several own values as an array. */
  private toSingleValue(values: string[]): ChipSelectValue {
    return values.length > 1 ? values : (values[0] ?? null);
  }

  private update(value: string | string[] | null): void {
    const normalized = Array.isArray(value) && !value.length ? null : value;
    this.value.set(normalized);
    this.onChange(normalized);
    this.onTouched();
  }
}
